import React, { useState, Suspense } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Package,
  Warehouse,
  Target,
  UserPlus,
  BadgePercent,
  Wallet,
  FileBarChart,
  LogOut,
  Menu,
  X,
  KeyRound,
  Loader2,
  MapPin,
  Bell,
  ChevronDown,
  Settings,
  HelpCircle,
  AlertTriangle,
  ShoppingBag,
  FileSignature,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const navSections = {
  executive: [
    { label: "Main", items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/leads", icon: UserPlus, label: "Walk-in Leads" },
      { to: "/billing", icon: ShoppingCart, label: "Billing" },
      { to: "/quotations", icon: FileSignature, label: "Quotations" },
      { to: "/targets", icon: Target, label: "Targets" },
    ]},
  ],
  adminManager: [
    { label: "Main", items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/leads", icon: UserPlus, label: "Walk-in Leads" },
      { to: "/invoices", icon: FileBarChart, label: "Invoices" },
      { to: "/billing", icon: ShoppingCart, label: "Billing" },
      { to: "/quotations", icon: FileSignature, label: "Quotations" },
    ]},
    { label: "Operations", items: [
      { to: "/inventory", icon: Warehouse, label: "Inventory" },
      { to: "/purchases", icon: ShoppingBag, label: "Purchases" },
      { to: "/damaged-goods", icon: AlertTriangle, label: "Damaged Goods" },
      { to: "/targets", icon: Target, label: "Targets" },
      { to: "/discount-approvals", icon: BadgePercent, label: "Discount Approvals" },
    ]},
    { label: "CRM", items: [
      { to: "/customers", icon: Users, label: "Customers" },
      { to: "/kitty", icon: Wallet, label: "Manager Kitty" },
      { to: "/reports", icon: FileBarChart, label: "Reports & Analytics" },
      { to: "/stores", icon: MapPin, label: "Stores" },
    ]},
  ],
  warehouseManager: [
    { label: "Main", items: [
      { to: "/invoices", icon: FileBarChart, label: "Invoices" },
    ]},
  ],
  adminOnly: [
    { to: "/users", icon: Users, label: "User Management" },
  ],
};

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [pwForm, setPwForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const isAdminOrManager = user?.role === "admin" || user?.role === "manager";
  const canSeeNotifications = isAdminOrManager || user?.role === "warehouse_manager";

  const fetchNotifications = async () => {
    if (!canSeeNotifications) return;
    try {
      const [notifsRes, countRes] = await Promise.all([
        apiClient.get("/notifications"),
        apiClient.get("/notifications/unread-count"),
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await apiClient.put("/notifications/mark-read");
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  // Poll notifications every 30s
  React.useEffect(() => {
    if (canSeeNotifications) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password || !pwForm.new_password) {
      toast.error("Please fill all fields");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setChangingPassword(true);
    try {
      await apiClient.put("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed successfully");
      setPasswordDialogOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const getNavSections = () => {
    if (user?.role === "warehouse_manager") return navSections.warehouseManager;
    if (user?.role === "executive") return navSections.executive;
    return navSections.adminManager;
  };

  const getAdminItems = () => {
    if (user?.role === "admin") return navSections.adminOnly;
    return [];
  };

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric"
  });

  const initials = user?.full_name
    ? user.full_name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  return (
    <div className="min-h-screen" style={{ background: "var(--page)" }}>
      {/* ═══ TOP NAVBAR ═══ */}
      <nav className="crm-nav" data-testid="crm-nav">
        <div className="crm-nav-brand">
          <button
            className="crm-mobile-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          <div className="crm-aden-logo" onClick={() => navigate("/dashboard")}>ADEN</div>
          <div className="crm-nav-divider" />
          <div>
            <div className="crm-nav-sub-title">AUTHORISED DISTRIBUTOR</div>
            <div className="crm-nav-sub-tag">ELECTRONICS & APPLIANCES</div>
          </div>
        </div>

        <div className="crm-nav-center hidden lg:block">
          {"\u0965\u0965"} Shree Nathji Krupa {"\u0965\u0965"}
        </div>

        <div className="crm-nav-right">
          <div className="crm-nav-date hidden md:block">{today}</div>
          {canSeeNotifications && (
            <div style={{ position: "relative" }}>
              <div
                className="crm-notif-btn"
                onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) markAllRead(); }}
                data-testid="notification-bell"
              >
                <Bell size={15} />
                {unreadCount > 0 && (
                  <span style={{ position: "absolute", top: "-3px", right: "-3px", width: "16px", height: "16px", borderRadius: "50%", background: "#e31e24", color: "#fff", fontSize: "9px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono'" }} data-testid="notif-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
              {notifOpen && (
                <>
                  <div style={{ position: "fixed", inset: 0, zIndex: 299 }} onClick={() => setNotifOpen(false)} />
                  <div style={{ position: "absolute", top: "42px", right: 0, width: "340px", maxHeight: "420px", background: "var(--white)", borderRadius: "var(--radius)", boxShadow: "var(--shadow-lg)", border: "1px solid var(--border-color)", zIndex: 300, overflow: "hidden", animation: "fadeIn .2s ease" }} data-testid="notif-dropdown">
                    <div style={{ padding: ".7rem 1rem", borderBottom: "1px solid var(--border-color)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--g50)" }}>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--g800)" }}>Notifications</span>
                      <span style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "'DM Mono'" }}>{notifications.length} total</span>
                    </div>
                    <div style={{ overflowY: "auto", maxHeight: "360px" }}>
                      {notifications.length === 0 ? (
                        <div style={{ padding: "2rem", textAlign: "center", color: "var(--muted)", fontSize: "12px" }}>No notifications yet</div>
                      ) : notifications.map(n => (
                        <div key={n.id} style={{ padding: ".6rem 1rem", borderBottom: "1px solid var(--g50)", cursor: "pointer", transition: "background .15s", background: (n.read_by || []).includes(user?.id) ? "transparent" : "var(--g50)" }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--g50)"}
                          onMouseLeave={e => e.currentTarget.style.background = (n.read_by || []).includes(user?.id) ? "transparent" : "var(--g50)"}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                            <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: n.type === "invoice" ? "var(--g500)" : "var(--warn)", flexShrink: 0 }} />
                            <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--ink)" }}>{n.title}</span>
                          </div>
                          <div style={{ fontSize: "11px", color: "var(--muted)", paddingLeft: "14px" }}>{n.message}</div>
                          <div style={{ fontSize: "9px", color: "var(--muted)", fontFamily: "'DM Mono'", paddingLeft: "14px", marginTop: "2px" }}>
                            {n.created_at ? new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {!canSeeNotifications && <div className="crm-notif-btn"><Bell size={15} /></div>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="crm-exec-selector" data-testid="user-menu-trigger">
                <div className="crm-exec-avatar">{initials}</div>
                <span className="crm-exec-name hidden sm:inline">{user?.full_name || "User"}</span>
                <ChevronDown size={10} style={{ color: "rgba(255,255,255,.4)" }} />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-3 py-2">
                <p className="font-semibold text-sm">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPasswordDialogOpen(true)}>
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* ═══ SHELL: SIDEBAR + CONTENT ═══ */}
      <div className="crm-shell">
        {/* Sidebar */}
        <aside
          className={`crm-sidebar ${sidebarOpen ? "open" : ""}`}
          data-testid="sidebar"
        >
          {getNavSections().map((section, sIdx) => (
            <div className="crm-sb-section" key={sIdx} style={sIdx === 0 ? { paddingTop: ".8rem" } : {}}>
              <div className="crm-sb-lbl">{section.label}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `crm-sb-link ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                >
                  <item.icon size={16} className="crm-sb-icon" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          {/* Admin-only items */}
          {getAdminItems().length > 0 && (
            <div className="crm-sb-section">
              <div className="crm-sb-lbl">Admin</div>
              {getAdminItems().map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `crm-sb-link ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon size={16} className="crm-sb-icon" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          )}

          {/* Bottom section */}
          <div className="crm-sidebar-bottom">
            <div
              className="crm-sidebar-bottom-item"
              onClick={() => setPasswordDialogOpen(true)}
              data-testid="change-password-btn"
            >
              <Settings size={14} />
              <span>Settings</span>
            </div>
            <div
              className="crm-sidebar-bottom-item"
              onClick={handleLogout}
            >
              <LogOut size={14} />
              <span>Logout</span>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="crm-sidebar-overlay open"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="crm-content">
          <Suspense fallback={
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
              <Loader2 className="animate-spin" style={{ color: "var(--g500, #16a34a)" }} size={28} />
            </div>
          }>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* ═══ CHANGE PASSWORD DIALOG ═══ */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current and new password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                placeholder="Min 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                placeholder="Re-enter new password"
                data-testid="confirm-new-password-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setPasswordDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-cta"
                disabled={changingPassword}
                data-testid="submit-change-password-btn"
              >
                {changingPassword && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
